import { useNotifications } from '@nohara-dxdev/react-core';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadAttachment } from '../api/upload-attachment';

import type { AttachmentFile } from '../types';

const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_DOC_SIZE = 5 * 1024 * 1024; // 5MB

export function useFileAttachments() {
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const notifications = useNotifications();
  const { t } = useTranslation();

  const addFiles = useCallback(
    (files: File[]) => {
      for (const file of files) {
        // Validate type
        if (!ALLOWED_TYPES.includes(file.type)) {
          notifications.error(t('aiChat.attachment.invalidType', { type: file.type }));
          continue;
        }

        // Validate size
        const isImage = file.type.startsWith('image/');
        const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_DOC_SIZE;
        if (file.size > maxSize) {
          notifications.error(t('aiChat.attachment.oversized'));
          continue;
        }

        // Generate preview URL
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        const previewUrl = URL.createObjectURL(file);
        setAttachments((prev) => [...prev, { file, previewUrl }]);
      }
    },
    [notifications, t],
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const item = prev[index];
      // eslint-disable-next-line n/no-unsupported-features/node-builtins
      URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const uploadAll = useCallback(
    async (
      orgId: string,
      projectId: string,
      sessionId: string,
    ): Promise<{ key: string; contentType: string }[]> => {
      const results = await Promise.all(
        attachments.map(async ({ file }, index) => {
          const key = await uploadAttachment(orgId, projectId, sessionId, file);
          // Update s3Key on successful upload
          setAttachments((prev) => {
            const updated = [...prev];
            if (updated[index]) {
              updated[index] = { ...updated[index], s3Key: key };
            }
            return updated;
          });
          return { key, contentType: file.type };
        }),
      );
      return results;
    },
    [attachments],
  );

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      for (const item of prev) {
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        URL.revokeObjectURL(item.previewUrl);
      }
      return [];
    });
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const imageFiles: File[] = [];

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        addFiles(imageFiles);
      }
    },
    [addFiles],
  );

  return {
    attachments,
    addFiles,
    removeAttachment,
    uploadAll,
    clearAttachments,
    handlePaste,
  };
}
